/**
 * useProfitDocuments — os documentos (orçamentos + ordens de serviço) que
 * alimentam o CMV da aba Lucro.
 *
 * Duas coisas que este hook existe para não deixar quebrar em silêncio:
 *
 * 1. **PAGINAÇÃO OBRIGATÓRIA (armadilha #50).** Um `select` sem `.range()`
 *    devolve no máximo 1.000 linhas e o PostgREST NÃO avisa que cortou: o
 *    relatório sairia com o CMV menor que a realidade, o lucro maior, e nenhum
 *    erro na tela. Aqui a leitura pagina até vir uma página incompleta e devolve
 *    `truncated` quando bate no teto — que a tela MOSTRA (no silent caps).
 *
 * 2. **Filtro largo no servidor, recorte exato no cliente.** A data de
 *    competência é `approved_at ?? created_at` (`timestamptz`), e o recorte
 *    final é feito no calendário LOCAL. Como o servidor compara em UTC, a
 *    janela SQL é alargada em um dia de cada lado para não perder as linhas de
 *    borda; quem decide de fato é `computeCogs`.
 *
 * Não é `useFinanceCategories` que semeia dados (armadilha #37): aqui só há
 * leitura.
 */
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/useCompany';
import { supabase } from '@/integrations/supabase/client';
import {
  toCogsDocumentFromQuote,
  toCogsDocumentFromServiceOrder,
  type CogsDocument,
} from '@/lib/profitReport';
import type { IsoDateRange } from '@/lib/paymentsReport';

const PAGE_SIZE = 1000;
/** Teto de segurança: 50 páginas = 50 mil documentos por período. */
const MAX_PAGES = 50;

export interface ProfitDocumentsResult {
  documents: CogsDocument[];
  /** `true` quando a leitura bateu no teto de páginas — os números estão incompletos. */
  truncated: boolean;
}

/** Um dia antes/depois, em 'YYYY-MM-DD' — a folga do filtro de servidor. */
function shiftIsoDate(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, (m ?? 1) - 1, d ?? 1));
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
}

async function fetchAllPages<T>(
  runPage: (from: number, to: number) => Promise<{ data: T[] | null; error: unknown }>,
): Promise<{ rows: T[]; truncated: boolean }> {
  const rows: T[] = [];
  for (let page = 0; page < MAX_PAGES; page += 1) {
    const start = page * PAGE_SIZE;
    const { data, error } = await runPage(start, start + PAGE_SIZE - 1);
    if (error) throw error;
    const batch = data ?? [];
    rows.push(...batch);
    // Página incompleta = acabou. É o único sinal confiável de fim.
    if (batch.length < PAGE_SIZE) return { rows, truncated: false };
  }
  return { rows, truncated: true };
}

export function useProfitDocuments(range: IsoDateRange, enabled: boolean) {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const companyId = activeCompany?.id ?? null;

  const from = shiftIsoDate(range.from, -1);
  const to = shiftIsoDate(range.to, 2); // +2: fim exclusivo do dia seguinte ao +1

  const query = useQuery<ProfitDocumentsResult>({
    queryKey: ['profit-documents', companyId, from, to],
    enabled: Boolean(user?.id && companyId) && enabled,
    networkMode: 'offlineFirst',
    staleTime: 60 * 1000,
    queryFn: async () => {
      if (!companyId) return { documents: [], truncated: false };

      // O filtro espelha `approved_at ?? created_at`: filtrar só por
      // `created_at` PERDERIA um orçamento antigo aprovado dentro do período —
      // justamente o que o CMV do período precisa contar.
      const quotes = await fetchAllPages<any>((start, end) =>
        supabase
          .from('quotes')
          .select('id, status, approved_at, created_at, quote_items(quantity, unit_cost, finance_entry_id)')
          .eq('company_id', companyId)
          .or(
            `and(approved_at.gte.${from},approved_at.lt.${to}),`
            + `and(approved_at.is.null,created_at.gte.${from},created_at.lt.${to})`,
          )
          .order('created_at', { ascending: true })
          .range(start, end) as any,
      );

      const orders = await fetchAllPages<any>((start, end) =>
        supabase
          .from('service_orders')
          .select('id, status, quote_id, completed_at, created_at, service_order_items(quantity, unit_cost)')
          .eq('company_id', companyId)
          .or(
            `and(completed_at.gte.${from},completed_at.lt.${to}),`
            + `and(completed_at.is.null,created_at.gte.${from},created_at.lt.${to})`,
          )
          .order('created_at', { ascending: true })
          .range(start, end) as any,
      );

      return {
        documents: [
          ...quotes.rows.map(toCogsDocumentFromQuote),
          ...orders.rows.map(toCogsDocumentFromServiceOrder),
        ],
        truncated: quotes.truncated || orders.truncated,
      };
    },
  });

  return {
    documents: query.data?.documents ?? [],
    truncated: query.data?.truncated ?? false,
    isLoading: query.isLoading,
    /**
     * Leitura falhou (tipicamente: sem rede — os documentos do CMV não são
     * cacheados como o financeiro). A tela PRECISA dizer isso: com a lista
     * vazia o CMV sai zero e o lucro aparece maior do que é, exatamente o
     * modo de falha que o `missingCostCount` existe para evitar.
     */
    isError: query.isError,
  };
}
